// viewport — キャンバス表示の座標変換モデル (docs/_wip/modern-phone-shell-2026-07-02.md
// §6 決定5、2026-09-29)。
//
// ## 背景
//
// 旧方式は `<main overflow-auto>` の `scrollLeft/scrollTop` で pan していた。この
// 方式は「canvas が表示枠より小さくなる (= 縮小時) とスクロール余地が無くなり、
// canvas が左上に固定されて動かせなくなる」構造的な弱点を持つ (staging 実機 fb
// 2026-09-29「縮小するとキャンバスが左上に寄って動かせない」)。
//
// 新方式 (B2、決定5) は表示状態を `{ tx, ty, zoom }` の座標変換に一本化する:
//   - `zoom` は従来どおり `CanvasDrawingEngine` が single source of truth (React
//     へミラー、キャンバス自体の `transform: scale(zoom)` も engine が適用する
//     ので不触)。
//   - `tx/ty` (= 表示枠の左上から canvas 左上までのオフセット、CSS px) は描画に
//     一切影響しない純粋な表示状態なので engine には持たせず、ここが唯一の
//     source of truth になる (`useRoomChrome` が所有・公開)。
//   - 表示枠 (`<main>`) は `overflow: hidden` にし、canvas を包む内側 wrapper に
//     `transform: translate3d(tx, ty, 0)` を当てて位置を決める。wrapper は
//     `items-start justify-start` の flex item のままなので、tx=ty=0 は「(padding
//     を除いた) 表示枠の左上」= 旧 scrollLeft=0 の見た目とちょうど一致する。
//
// ## 座標系
//
// このモジュールの `tx/ty` および `anchorX/anchorY`・`slotWidth/slotHeight` は
// すべて「表示枠の **content box** (= padding を除いた内側) の左上を原点とする
// CSS px」で統一する。border-box 基準の値 (`getBoundingClientRect` や
// `clientWidth`) から変換するときは呼び出し側で padding を差し引く
// (`measureSlot` がその変換を担う)。
//
// ## 変換行列 (2026-10-03 canvas-rotation 段取り 1、docs/_wip/canvas-rotation-2026-10-03.md)
//
// 表示の回転を入れても入力・overlay が壊れないよう、「画面座標 ⇔ canvas 座標」の
// 変換を `ViewTransform` (= `{tx, ty, zoom, rotation, flipX}` + canvas 寸法) の
// **このファイル 1 か所**に集約した (§1b)。旧来は入力 (`DrawingCanvas.toCanvasXY`)
// ・ブラシカーソル・imagepaste・Undo ハイライト等がそれぞれ `getBoundingClientRect`
// の比率計算 + `width - x` の flip 補正で独自に計算していたが、回転すると
// `getBoundingClientRect` は軸に沿った外接矩形に膨らみ比率計算が成り立たない。
//
// 座標は 4 つの空間をこの順に変換する:
//   canvas  — 真の canvas ピクセル (engine が記録する座標、flip / zoom / 回転と無関係)
//   local   — 表示 wrapper (`chrome.viewportLayerRef`) のローカル CSS px。
//             flip と zoom だけを適用した「回転・平行移動前」の空間
//             (= canvas 要素と overlay 群が実際に配置される座標系)。
//   slot    — 表示枠 (`<main>`) の content box 左上を原点とする CSS px (回転・平行移動後)。
//   client  — ブラウザ viewport 座標 (`PointerEvent.clientX/Y`)。
//
//   local = flipX ? ((W - x) * zoom, y * zoom) : (x * zoom, y * zoom)
//   slot  = (tx, ty) + c + R(rotation) * (local - c),  c = (W*zoom/2, H*zoom/2)
//   client = slotOrigin + slot
//
// 回転 R は wrapper box の中心 c まわり、`rotation` は度・時計回り正 (CSS `rotate()` と同じ)。
// DOM 上は `translate3d(tx,ty,0) rotate(θ)` (transform-origin は既定の 50% 50% = c) と
// 一致する。`rotation === 0` のとき c の加減算を一切行わない (= 旧来の浮動小数演算と
// ビット一致、回転 0 の見た目・挙動を厳密に不変に保つため)。
// `<main>` 自体は回転しないので `slotOrigin` は回転に影響されない (外接矩形に膨らまない)。
//
// ## このファイルの構成
//
// 前半 (§1) は DOM に触れない純粋関数 (= vitest node env でテスト対象、
// `viewport.test.ts`)。後半 (§2) は 2 つだけの薄い DOM ヘルパ
// (`measureSlot` / `applyViewportTransform`) — 呼び出し側 (`CanvasStage` /
// `useRoomChrome`) がこれらを毎回書くと padding 読み取り・transform 文字列の
// 表記が呼び出し箇所ごとに揺れるため、1 箇所に集約する (DOM 依存なのでこの
// 2 関数だけは unit test 対象外)。

// ─── §1. 純粋ロジック ──────────────────────────────────────────────────────

/** 各辺で保証する canvas の最小可視 px (決定5「見失わない」)。 */
export const MIN_VISIBLE_CANVAS_PX = 64;

/** 表示状態の pan オフセット (CSS px、表示枠 content box 左上が原点)。 */
export interface ViewportOffset {
  readonly tx: number;
  readonly ty: number;
}

/** 原点 (= 追加オフセットなし) の viewport。engine 差し替え時の reset 値。 */
export const ORIGIN_VIEWPORT: ViewportOffset = { tx: 0, ty: 0 };

/**
 * 1 軸ぶんの offset を「canvas (長さ `size`, = canvasDimension*zoom) が
 * `slotSize` の中で最低 `minVisible` px は可視」という制約に収める。
 *
 * - `offset` が小さすぎる (canvas を右/下へ動かしすぎ) と canvas 右/下端が
 *   `minVisible` を割るので `minVisible - size` で止める。
 * - `offset` が大きすぎる (canvas を左/上へ動かしすぎ) と canvas 左/上端側の
 *   可視量が `minVisible` を割るので `slotSize - minVisible` で止める。
 * - 両方を同時に満たせない (= `slotSize + size` が `minVisible*2` 未満、
 *   極端に小さい枠/canvas の組み合わせ) 場合は centering にフォールバックする
 *   (= 詰みを避け、常に何らかの妥当な位置を返す)。
 * - 戻り値は整数 CSS px に丸める (fable-advisor 指摘、2026-09-29): centering
 *   計算 (`(slotSize - size) / 2`) は奇数差で小数 (例 200.5) になりうる。旧
 *   scrollLeft は常に整数だったのに対し、`translate3d` に小数を渡すと
 *   canvas 自身の `transform: scale(zoom)` (engine 側、整数 zoom でも) と
 *   組み合わさってサブピクセル配置になり、dpr=1 の PC で線が僅かに滲む
 *   (= 決定2(c) が優先した「dpr=1 PC でのピクセル均一」を壊しかねない)。
 *   この関数がすべての書き込み経路 (`clampViewport` 経由) の最終ゲートなので
 *   ここ 1 箇所で丸めれば全経路に効く。web package は他の zoom/pixel 計算でも
 *   dpr を掛けない方針 (= CSS px == buffer px、DrawingCanvas.tsx 既存コメント)
 *   なので、ここも単純な整数丸め (dpr 非依存) に揃える。
 */
export const clampViewportAxis = (
  offset: number,
  size: number,
  slotSize: number,
  minVisible: number = MIN_VISIBLE_CANVAS_PX,
): number => {
  if (!Number.isFinite(offset)) return 0;
  const minOffset = minVisible - size;
  const maxOffset = slotSize - minVisible;
  if (minOffset > maxOffset) {
    return Math.round((slotSize - size) / 2);
  }
  return Math.round(Math.max(minOffset, Math.min(offset, maxOffset)));
};

/** `clampViewportAxis` を x/y 両軸に適用する。 */
export const clampViewport = (
  tx: number,
  ty: number,
  canvasWidth: number,
  canvasHeight: number,
  zoom: number,
  slotWidth: number,
  slotHeight: number,
  minVisible: number = MIN_VISIBLE_CANVAS_PX,
): ViewportOffset => {
  const z = zoom > 0 ? zoom : 1;
  return {
    tx: clampViewportAxis(tx, canvasWidth * z, slotWidth, minVisible),
    ty: clampViewportAxis(ty, canvasHeight * z, slotHeight, minVisible),
  };
};

/**
 * 入室時 (1x) / 全体表示 (フィット) の初期位置 (決定5「入室時と全体表示は、
 * canvas が枠より小さい軸は中央寄せ。大きい軸は従来の見え方 (左上 + 余白)」)。
 *
 * - 軸ごとに canvas (= size = canvasDimension*zoom) が slot より小さければ
 *   中央寄せ、大きければ 0 (= 左上、wrapper の flex 開始位置がそのまま padding
 *   分の余白になる)。
 * - 最後に `clampViewport` を通す (defensive、通常は既に範囲内)。
 */
export const initialViewport = (
  canvasWidth: number,
  canvasHeight: number,
  zoom: number,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  const z = zoom > 0 ? zoom : 1;
  const axisInitial = (size: number, slotSize: number): number =>
    size <= slotSize ? (slotSize - size) / 2 : 0;
  const rawTx = axisInitial(canvasWidth * z, slotWidth);
  const rawTy = axisInitial(canvasHeight * z, slotHeight);
  return clampViewport(rawTx, rawTy, canvasWidth, canvasHeight, z, slotWidth, slotHeight);
};

/**
 * ズームのアンカー固定 (決定5「ズームのアンカーはホイール=ポインタ / ピンチ=重心 /
 * ボタン・キー=枠中央」)。`anchorX/Y` (= slot content box 座標) の真下にある
 * canvas 座標が、zoom 変更後も同じ画面位置に留まるよう新しい tx/ty を返す。
 *
 * 旧 scroll 方式の `useLayoutEffect` 補正 (α-feedback K) と違い、tx/ty は
 * レイアウト (要素サイズ) に依存しないので **同期的に** 計算できる — DOM の
 * resize を待つ必要がない (旧方式は `innerWrapper` の width/height が
 * `canvas*zoom` に連動していたため、zoom 変更 → resize → scroll 補正の順で
 * paint 前に効かせる必要があった)。
 */
export const viewportAfterZoom = (
  prev: ViewportOffset,
  prevZoom: number,
  nextZoom: number,
  anchorX: number,
  anchorY: number,
  canvasWidth: number,
  canvasHeight: number,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  const z0 = prevZoom > 0 ? prevZoom : 1;
  const z1 = nextZoom > 0 ? nextZoom : 1;
  const canvasX = (anchorX - prev.tx) / z0;
  const canvasY = (anchorY - prev.ty) / z0;
  const rawTx = anchorX - canvasX * z1;
  const rawTy = anchorY - canvasY * z1;
  return clampViewport(rawTx, rawTy, canvasWidth, canvasHeight, z1, slotWidth, slotHeight);
};

/**
 * 表示枠 (slot) のサイズが変わったとき (パネル開閉・回転・ウィンドウ resize・
 * Classic⇄Modern 切替、決定5「枠 resize 時は枠中央の canvas 点を保つ」)、
 * **旧 slot の中央にあった canvas 点** が新 slot の中央にも留まるよう tx/ty を
 * 再計算する。zoom は不変 (resize は zoom を変えない)。
 */
export const viewportAfterResize = (
  prev: ViewportOffset,
  zoom: number,
  canvasWidth: number,
  canvasHeight: number,
  prevSlotWidth: number,
  prevSlotHeight: number,
  nextSlotWidth: number,
  nextSlotHeight: number,
): ViewportOffset => {
  const z = zoom > 0 ? zoom : 1;
  const canvasX = (prevSlotWidth / 2 - prev.tx) / z;
  const canvasY = (prevSlotHeight / 2 - prev.ty) / z;
  const rawTx = nextSlotWidth / 2 - canvasX * z;
  const rawTy = nextSlotHeight / 2 - canvasY * z;
  return clampViewport(rawTx, rawTy, canvasWidth, canvasHeight, z, nextSlotWidth, nextSlotHeight);
};

/**
 * 左右反転 (flipX) トグル時の水平位置補正 (旧 `zoomScroll.ts` の
 * `scrollForFlipMirror` 相当)。
 *
 * 反転は canvas 自身の CSS transform (engine 適用の `scale(-zoom, zoom)` +
 * 補正 translateX) で実現され、canvas の描画箱が wrapper ローカル座標系で
 * 占める矩形は反転有無に関わらず不変 — しかし「wrapper 上のある画面位置に
 * 表示される canvas 座標」の対応は反転で変わる (鏡像になる) ため、tx を
 * 据え置くと「反転した瞬間に見ている絵の部分が変わる」体感になる
 * (旧版 α-feedback で対応済みの問題、新方式でも同じ理屈で再発するため移植)。
 *
 * 反転前に画面中央にあった canvas 座標 `cx` を求め、反転後も同じ `cx` が
 * 画面中央に来る tx を逆算する。垂直方向 (ty) は反転に無関係なので不変。
 */
export const viewportAfterFlipToggle = (
  prev: ViewportOffset,
  zoom: number,
  wasFlipX: boolean,
  nextFlipX: boolean,
  canvasWidth: number,
  canvasHeight: number,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  if (wasFlipX === nextFlipX) return prev;
  const z = zoom > 0 ? zoom : 1;
  const centerX = slotWidth / 2;
  const cx = wasFlipX ? canvasWidth - (centerX - prev.tx) / z : (centerX - prev.tx) / z;
  const rawTx = nextFlipX ? centerX - (canvasWidth - cx) * z : centerX - cx * z;
  return clampViewport(rawTx, prev.ty, canvasWidth, canvasHeight, z, slotWidth, slotHeight);
};

// ─── §1b. 画面 ⇔ canvas 座標の変換行列 (純粋関数) ─────────────────────────

/** 2 次元の点 (空間は関数名・型の文脈で決まる)。 */
export interface ViewPoint {
  readonly x: number;
  readonly y: number;
}

/** 軸に沿った矩形 (CSS px)。 */
export interface ViewRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * 表示変換の全パラメータ。`zoom` / `flipX` は `CanvasDrawingEngine`、`tx/ty` は
 * `useRoomChrome.viewportRef`、`rotation` は `useRoomChrome` が持つ表示回転
 * (段取り 2、tx/ty と同じく表示専用 transient state)。canvas 寸法は回転中心 c と flip の
 * 反射軸に使う。
 */
export interface ViewTransform {
  readonly tx: number;
  readonly ty: number;
  readonly zoom: number;
  /** 度、時計回り正 (`[0, 360)`)。0 のとき旧来の演算とビット一致。 */
  readonly rotation: number;
  readonly flipX: boolean;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
}

const safeZoom = (v: ViewTransform): number => (v.zoom > 0 ? v.zoom : 1);

/** (rotation=0 でない場合のみ) wrapper 中心まわりに点を `deg` 度回す。 */
const rotateAround = (p: ViewPoint, cx: number, cy: number, deg: number): ViewPoint => {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - cx;
  const dy = p.y - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
};

/** canvas → local (flip + zoom)。overlay (wrapper の子) の配置はこれ。 */
export const canvasToLocal = (v: ViewTransform, x: number, y: number): ViewPoint => {
  const z = safeZoom(v);
  return { x: (v.flipX ? v.canvasWidth - x : x) * z, y: y * z };
};

/**
 * zoom / flip だけから canvas → local の変換を行う簡易版 (pan・回転を持たない
 * wrapper の子 overlay 用: `NameLabelOverlay` / `DraftBubbleOverlay`)。
 * `canvasWidth` 未指定なら反転しない (= 旧 overlay の防御挙動を踏襲)。
 * 回転は wrapper ごと回るので local 座標は回転に無関係 (= ここに rotation は不要)。
 */
export const canvasToLocalByZoomFlip = (
  x: number,
  y: number,
  zoom: number,
  flipX = false,
  canvasWidth?: number,
): ViewPoint =>
  canvasToLocal(
    {
      tx: 0,
      ty: 0,
      zoom,
      rotation: 0,
      flipX: flipX && canvasWidth !== undefined,
      canvasWidth: canvasWidth ?? 0,
      canvasHeight: 0,
    },
    x,
    y,
  );

/** local → canvas (`canvasToLocal` の逆)。 */
export const localToCanvas = (v: ViewTransform, lx: number, ly: number): ViewPoint => {
  const z = safeZoom(v);
  const x = lx / z;
  return { x: v.flipX ? v.canvasWidth - x : x, y: ly / z };
};

/** local → slot (回転 + 平行移動)。 */
export const localToSlot = (v: ViewTransform, lx: number, ly: number): ViewPoint => {
  if (v.rotation === 0) return { x: lx + v.tx, y: ly + v.ty };
  const z = safeZoom(v);
  const r = rotateAround(
    { x: lx, y: ly },
    (v.canvasWidth * z) / 2,
    (v.canvasHeight * z) / 2,
    v.rotation,
  );
  return { x: r.x + v.tx, y: r.y + v.ty };
};

/** slot → local (`localToSlot` の逆)。 */
export const slotToLocal = (v: ViewTransform, sx: number, sy: number): ViewPoint => {
  if (v.rotation === 0) return { x: sx - v.tx, y: sy - v.ty };
  const z = safeZoom(v);
  return rotateAround(
    { x: sx - v.tx, y: sy - v.ty },
    (v.canvasWidth * z) / 2,
    (v.canvasHeight * z) / 2,
    -v.rotation,
  );
};

/** canvas → slot。 */
export const canvasToSlot = (v: ViewTransform, x: number, y: number): ViewPoint => {
  const l = canvasToLocal(v, x, y);
  return localToSlot(v, l.x, l.y);
};

/** slot → canvas。 */
export const slotToCanvas = (v: ViewTransform, sx: number, sy: number): ViewPoint => {
  const l = slotToLocal(v, sx, sy);
  return localToCanvas(v, l.x, l.y);
};

/**
 * canvas 空間の矩形 `(x, y, w, h)` (左上 + 幅高さ、flip 前の真の座標) が slot 空間で
 * 占める**外接矩形**。回転 0 では厳密に flip/zoom/平行移動後の矩形そのもの。
 * 4 隅を変換して min/max を取るので flip・回転のどちらでも向きの入れ替わりを吸収する。
 */
export const canvasRectToSlotBounds = (
  v: ViewTransform,
  x: number,
  y: number,
  w: number,
  h: number,
): ViewRect => {
  const corners = [
    canvasToSlot(v, x, y),
    canvasToSlot(v, x + w, y),
    canvasToSlot(v, x, y + h),
    canvasToSlot(v, x + w, y + h),
  ];
  let left = corners[0]?.x ?? 0;
  let right = left;
  let top = corners[0]?.y ?? 0;
  let bottom = top;
  for (const c of corners) {
    if (c.x < left) left = c.x;
    if (c.x > right) right = c.x;
    if (c.y < top) top = c.y;
    if (c.y > bottom) bottom = c.y;
  }
  return { left, top, width: right - left, height: bottom - top };
};

/**
 * 表示枠 (slot、`slotWidth × slotHeight`) に今映っている領域の 4 隅を canvas 座標で返す
 * (左上・右上・右下・左下の順、slot の隅に対応)。回転 0 / flip なしでは軸に沿った
 * 矩形、回転中は傾いた四角形になる (ナビ窓の表示範囲枠の元データ)。
 */
export const visibleCanvasQuad = (
  v: ViewTransform,
  slotWidth: number,
  slotHeight: number,
): readonly [ViewPoint, ViewPoint, ViewPoint, ViewPoint] => [
  slotToCanvas(v, 0, 0),
  slotToCanvas(v, slotWidth, 0),
  slotToCanvas(v, slotWidth, slotHeight),
  slotToCanvas(v, 0, slotHeight),
];

/**
 * canvas 座標 `(cx, cy)` を slot の中央に置く `tx/ty` (zoom / flip / rotation は
 * `v` のものを使い、`v.tx/ty` は無視する)。ナビ窓クリック・「ここへ移動」系の逆算用。
 * clamp は行わない (呼び出し側が `clampViewport` を通す)。
 */
export const viewportCenteredOn = (
  v: ViewTransform,
  cx: number,
  cy: number,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  const withoutOffset: ViewTransform = { ...v, tx: 0, ty: 0 };
  const s = canvasToSlot(withoutOffset, cx, cy);
  return { tx: slotWidth / 2 - s.x, ty: slotHeight / 2 - s.y };
};

/**
 * 表示枠 content box の原点の client 座標 (`slotOrigin`) を足し引きして
 * slot ⇔ client を変換する。
 */
export const slotToClient = (origin: ViewPoint, sx: number, sy: number): ViewPoint => ({
  x: origin.x + sx,
  y: origin.y + sy,
});
export const clientToSlot = (origin: ViewPoint, clientX: number, clientY: number): ViewPoint => ({
  x: clientX - origin.x,
  y: clientY - origin.y,
});

/** client → canvas (ポインタ入力用)。 */
export const clientToCanvas = (
  v: ViewTransform,
  origin: ViewPoint,
  clientX: number,
  clientY: number,
): ViewPoint => {
  const s = clientToSlot(origin, clientX, clientY);
  return slotToCanvas(v, s.x, s.y);
};

/** canvas → client (viewport 固定配置の overlay 用)。 */
export const canvasToClient = (
  v: ViewTransform,
  origin: ViewPoint,
  x: number,
  y: number,
): ViewPoint => {
  const s = canvasToSlot(v, x, y);
  return slotToClient(origin, s.x, s.y);
};

/** canvas 矩形 → client 外接矩形 (`canvasRectToSlotBounds` + origin)。 */
export const canvasRectToClientBounds = (
  v: ViewTransform,
  origin: ViewPoint,
  x: number,
  y: number,
  w: number,
  h: number,
): ViewRect => {
  const b = canvasRectToSlotBounds(v, x, y, w, h);
  return { left: b.left + origin.x, top: b.top + origin.y, width: b.width, height: b.height };
};

// ─── §1c. 回転対応の viewport 計算 (純粋関数、段取り 2) ──────────────────

/**
 * canvas 座標 `(cx, cy)` が slot 座標 `(sx, sy)` に来るような `tx/ty` を返す
 * (`v` の zoom / flip / rotation を使い、`v.tx/ty` は無視する)。zoom・回転・resize の
 * 「アンカー固定」の共通部品。clamp は行わない。
 */
export const viewportKeepingCanvasPoint = (
  v: ViewTransform,
  cx: number,
  cy: number,
  sx: number,
  sy: number,
): ViewportOffset => {
  const s = canvasToSlot({ ...v, tx: 0, ty: 0 }, cx, cy);
  return { tx: sx - s.x, ty: sy - s.y };
};

type Poly = ReadonlyArray<ViewPoint>;

/** 凸多角形 `poly` を軸 `(ax, ay)` に射影した [min, max]。 */
const project = (poly: Poly, ax: number, ay: number): [number, number] => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const p of poly) {
    const d = p.x * ax + p.y * ay;
    if (d < min) min = d;
    if (d > max) max = d;
  }
  return [min, max];
};

/** 凸多角形同士が (接触を含めて) 重なるか (SAT)。`axes` は呼び出し側が候補軸を渡す。 */
const convexOverlap = (a: Poly, b: Poly): boolean => {
  const axesOf = (poly: Poly): Array<[number, number]> => {
    const out: Array<[number, number]> = [];
    for (let i = 0; i < poly.length; i++) {
      const p = poly[i];
      const q = poly[(i + 1) % poly.length];
      if (p === undefined || q === undefined) continue;
      out.push([-(q.y - p.y), q.x - p.x]);
    }
    return out;
  };
  for (const [ax, ay] of [...axesOf(a), ...axesOf(b)]) {
    const [aMin, aMax] = project(a, ax, ay);
    const [bMin, bMax] = project(b, ax, ay);
    if (aMax < bMin || bMax < aMin) return false;
  }
  return true;
};

/** canvas 全体 4 隅の slot 座標 (左上・右上・右下・左下)。 */
const canvasQuadInSlot = (v: ViewTransform): Poly => [
  canvasToSlot(v, 0, 0),
  canvasToSlot(v, v.canvasWidth, 0),
  canvasToSlot(v, v.canvasWidth, v.canvasHeight),
  canvasToSlot(v, 0, v.canvasHeight),
];

/**
 * 回転を含む `v` (tx/ty を含む) の clamp (決定5「各辺 `minVisible` px は見える」の回転版)。
 *
 * 回転 0 は従来の `clampViewport` にそのまま委譲する (= 整数丸め込みで旧挙動と一致)。
 * 回転中は「canvas (傾いた四角形) が、slot を `minVisible` ぶん内側に縮めた矩形と重なる」
 * を条件にする — 回転 0 では `clampViewportAxis` の「各辺 `minVisible` 可視」と同値になる
 * (canvas の外接矩形が内側矩形と重なる ⇔ 左端 <= slot-m かつ 右端 >= m)。満たさなければ
 * 現在位置と「canvas 中心を slot 中心に置く位置」を結ぶ線上で、条件を満たす最小の補正
 * (二分探索、凸集合なので区間になる) へ戻す。縮めた矩形が潰れる極小枠は中央寄せ。
 */
export const clampViewportForTransform = (
  v: ViewTransform,
  slotWidth: number,
  slotHeight: number,
  minVisible: number = MIN_VISIBLE_CANVAS_PX,
): ViewportOffset => {
  if (v.rotation === 0) {
    return clampViewport(
      v.tx,
      v.ty,
      v.canvasWidth,
      v.canvasHeight,
      v.zoom,
      slotWidth,
      slotHeight,
      minVisible,
    );
  }
  if (!Number.isFinite(v.tx) || !Number.isFinite(v.ty)) {
    return viewportKeepingCanvasPoint(
      v,
      v.canvasWidth / 2,
      v.canvasHeight / 2,
      slotWidth / 2,
      slotHeight / 2,
    );
  }
  const centered = viewportKeepingCanvasPoint(
    v,
    v.canvasWidth / 2,
    v.canvasHeight / 2,
    slotWidth / 2,
    slotHeight / 2,
  );
  const m = minVisible;
  if (slotWidth - 2 * m <= 0 || slotHeight - 2 * m <= 0) return centered;
  const inner: Poly = [
    { x: m, y: m },
    { x: slotWidth - m, y: m },
    { x: slotWidth - m, y: slotHeight - m },
    { x: m, y: slotHeight - m },
  ];
  const ok = (tx: number, ty: number): boolean =>
    convexOverlap(canvasQuadInSlot({ ...v, tx, ty }), inner);
  if (ok(v.tx, v.ty)) return { tx: v.tx, ty: v.ty };
  let lo = 0; // 条件を満たさない側 (現在位置)
  let hi = 1; // 満たす側 (中央寄せ)
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (ok(v.tx + (centered.tx - v.tx) * mid, v.ty + (centered.ty - v.ty) * mid)) hi = mid;
    else lo = mid;
  }
  return { tx: v.tx + (centered.tx - v.tx) * hi, ty: v.ty + (centered.ty - v.ty) * hi };
};

/**
 * 回転量を `nextRotation` に変え、slot 座標 `(pivotX, pivotY)` の真下にある canvas 点が
 * 動かないよう tx/ty を返す (ひねりジェスチャ = 2 本指の重心 / PC ドラッグ・ボタン =
 * 枠中央)。`v` は変更前の変換 (zoom / flip / 現在の rotation / tx / ty)。clamp 済み。
 */
export const viewportAfterRotation = (
  v: ViewTransform,
  nextRotation: number,
  pivotX: number,
  pivotY: number,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  const c = slotToCanvas(v, pivotX, pivotY);
  const next: ViewTransform = { ...v, rotation: nextRotation };
  const o = viewportKeepingCanvasPoint(next, c.x, c.y, pivotX, pivotY);
  return clampViewportForTransform({ ...next, tx: o.tx, ty: o.ty }, slotWidth, slotHeight);
};

/**
 * 回転対応のズームアンカー固定。`v` は変更前の変換 (**旧** zoom を含む)。回転 0 は
 * 従来の `viewportAfterZoom` に委譲 (ビット一致)。
 */
export const viewportAfterZoomRotated = (
  v: ViewTransform,
  nextZoom: number,
  anchorX: number,
  anchorY: number,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  if (v.rotation === 0) {
    return viewportAfterZoom(
      v,
      v.zoom,
      nextZoom,
      anchorX,
      anchorY,
      v.canvasWidth,
      v.canvasHeight,
      slotWidth,
      slotHeight,
    );
  }
  const c = slotToCanvas(v, anchorX, anchorY);
  const next: ViewTransform = { ...v, zoom: nextZoom };
  const o = viewportKeepingCanvasPoint(next, c.x, c.y, anchorX, anchorY);
  return clampViewportForTransform({ ...next, tx: o.tx, ty: o.ty }, slotWidth, slotHeight);
};

/** 回転対応の枠 resize 追従 (旧 slot 中央の canvas 点を新 slot 中央に保つ)。 */
export const viewportAfterResizeRotated = (
  v: ViewTransform,
  prevSlotWidth: number,
  prevSlotHeight: number,
  nextSlotWidth: number,
  nextSlotHeight: number,
): ViewportOffset => {
  if (v.rotation === 0) {
    return viewportAfterResize(
      v,
      v.zoom,
      v.canvasWidth,
      v.canvasHeight,
      prevSlotWidth,
      prevSlotHeight,
      nextSlotWidth,
      nextSlotHeight,
    );
  }
  const c = slotToCanvas(v, prevSlotWidth / 2, prevSlotHeight / 2);
  const o = viewportKeepingCanvasPoint(v, c.x, c.y, nextSlotWidth / 2, nextSlotHeight / 2);
  return clampViewportForTransform({ ...v, tx: o.tx, ty: o.ty }, nextSlotWidth, nextSlotHeight);
};

/**
 * 回転対応の反転トグル補正。`v` は反転前の変換 (`v.flipX` が toggle 前の値)。
 * 反転前に枠中央にあった canvas 点が反転後も枠中央に留まる。回転 0 は従来関数に委譲。
 */
export const viewportAfterFlipToggleRotated = (
  v: ViewTransform,
  nextFlipX: boolean,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  if (v.rotation === 0) {
    return viewportAfterFlipToggle(
      v,
      v.zoom,
      v.flipX,
      nextFlipX,
      v.canvasWidth,
      v.canvasHeight,
      slotWidth,
      slotHeight,
    );
  }
  if (v.flipX === nextFlipX) return { tx: v.tx, ty: v.ty };
  const c = slotToCanvas(v, slotWidth / 2, slotHeight / 2);
  const next: ViewTransform = { ...v, flipX: nextFlipX };
  const o = viewportKeepingCanvasPoint(next, c.x, c.y, slotWidth / 2, slotHeight / 2);
  return clampViewportForTransform({ ...next, tx: o.tx, ty: o.ty }, slotWidth, slotHeight);
};

/**
 * 入力・overlay が使う変換の窓口。`ViewTransform` と slot 原点の「最新値」を毎回
 * 取得する getter から組み立てる (= tx/ty/zoom/flip は高頻度に変わり、React の
 * render を待たず常に最新で計算したいため、値ではなく getter を束ねる)。
 * `DrawingCanvas` / 各 overlay はこの interface だけを見て、`getBoundingClientRect` の
 * 比率計算や `width - x` の flip 補正を自前で持たない。
 */
export interface CanvasViewMapper {
  getTransform(): ViewTransform;
  /** 表示枠 content box 原点の client 座標。表示枠が未 mount なら (0,0)。 */
  getSlotOrigin(): ViewPoint;
  clientToCanvas(clientX: number, clientY: number): ViewPoint;
  canvasToClient(x: number, y: number): ViewPoint;
  /** client → wrapper-local CSS px (wrapper の子 overlay の `left/top` 用)。 */
  clientToLocal(clientX: number, clientY: number): ViewPoint;
  canvasToLocal(x: number, y: number): ViewPoint;
  canvasRectToClientBounds(x: number, y: number, w: number, h: number): ViewRect;
  /**
   * 表示 (pan / zoom / 回転) が変わったら呼ばれる listener を登録する。戻り値で解除。
   * ポインタ位置に張り付く overlay (ブラシカーソル等) が、ポインタが止まったままの
   * ズームでも位置を計算し直すために使う。
   */
  onViewChange(listener: () => void): () => void;
}

export interface CanvasViewMapperDeps {
  readonly getTransform: () => ViewTransform;
  readonly getSlotOrigin: () => ViewPoint | null;
  /** 表示変更の購読 (省略時は通知なし = テスト用の固定 mapper)。 */
  readonly subscribe?: (listener: () => void) => () => void;
}

const ZERO_POINT: ViewPoint = { x: 0, y: 0 };

export const createViewMapper = (deps: CanvasViewMapperDeps): CanvasViewMapper => {
  const origin = (): ViewPoint => deps.getSlotOrigin() ?? ZERO_POINT;
  return {
    getTransform: deps.getTransform,
    getSlotOrigin: origin,
    clientToCanvas: (cx, cy) => clientToCanvas(deps.getTransform(), origin(), cx, cy),
    canvasToClient: (x, y) => canvasToClient(deps.getTransform(), origin(), x, y),
    clientToLocal: (cx, cy) => {
      const o = origin();
      return slotToLocal(deps.getTransform(), cx - o.x, cy - o.y);
    },
    canvasToLocal: (x, y) => canvasToLocal(deps.getTransform(), x, y),
    canvasRectToClientBounds: (x, y, w, h) =>
      canvasRectToClientBounds(deps.getTransform(), origin(), x, y, w, h),
    onViewChange: (listener) => deps.subscribe?.(listener) ?? (() => {}),
  };
};

// ─── §2. 薄い DOM ヘルパ (unit test 対象外) ──────────────────────────────

/** `measureSlot` の戻り値 (= 表示枠の padding + content box 実寸)。 */
export interface SlotMetrics {
  /** `<main>` の padding-left (px)。border-box → content-box 座標変換に使う。 */
  readonly padX: number;
  /** `<main>` の padding-top (px)。 */
  readonly padY: number;
  /** content box の幅 (= `clientWidth - padLeft - padRight`)。このモジュールの `slotWidth`。 */
  readonly slotWidth: number;
  /** content box の高さ。 */
  readonly slotHeight: number;
}

/**
 * 表示枠 (`<main>`) の computed padding から `SlotMetrics` を実測する。
 * `clientWidth/Height` は padding を含む border-box 内寸なので、ここで
 * padding を差し引いて「canvas を置ける content box」の実寸にする。
 */
export const measureSlot = (wrapper: HTMLElement): SlotMetrics => {
  const cs = getComputedStyle(wrapper);
  const padLeft = Number.parseFloat(cs.paddingLeft) || 0;
  const padTop = Number.parseFloat(cs.paddingTop) || 0;
  const padRight = Number.parseFloat(cs.paddingRight) || 0;
  const padBottom = Number.parseFloat(cs.paddingBottom) || 0;
  return {
    padX: padLeft,
    padY: padTop,
    slotWidth: Math.max(0, wrapper.clientWidth - padLeft - padRight),
    slotHeight: Math.max(0, wrapper.clientHeight - padTop - padBottom),
  };
};

/**
 * 表示枠 (`<main>`) の content box 原点の client 座標 (= §1b の `slotOrigin`)。
 * border (`clientLeft/Top`) + padding を足し、programmatic scroll の残り
 * (`scrollLeft/Top`、`overflow: hidden` でも focus 等で動きうる) を引く。
 * `<main>` は回転しないので、wrapper 側の回転と無関係に常に正しい (外接矩形に
 * 膨らむのは回転する wrapper の `getBoundingClientRect` であって、これではない)。
 */
export const measureSlotOrigin = (slotEl: HTMLElement): ViewPoint => {
  const r = slotEl.getBoundingClientRect();
  const m = measureSlot(slotEl);
  return {
    x: r.left + slotEl.clientLeft + m.padX - slotEl.scrollLeft,
    y: r.top + slotEl.clientTop + m.padY - slotEl.scrollTop,
  };
};

/**
 * `ViewportOffset` を DOM へ適用する唯一の書き込み経路。`el` は canvas +
 * overlay 群を包む wrapper (= `CanvasStage` の inner wrapper、
 * `chrome.viewportLayerRef`)。React の `style` prop はこの要素の `transform`
 * を一切管理しない (JSX 側で指定しない) ので、この直接書き込みは再レンダリング
 * で上書きされない (= 旧 `wrapper.scrollLeft` 直書きと同じ perf 特性: 高頻度
 * 更新 — ドラッグ pan / ピンチ — でも React の setState/re-render を挟まない)。
 * `el` が未 mount (null) のときは値の保持 (`viewportRef.current` 更新) だけ
 * 呼び出し側が行い、ここは何もしない。
 */
export const applyViewportTransform = (
  el: HTMLElement | null,
  tx: number,
  ty: number,
  rotation = 0,
  center: { readonly cx: number; readonly cy: number } = { cx: 0, cy: 0 },
): void => {
  if (el === null) return;
  // rotation=0 では従来と完全に同じ文字列。回転の中心 c (= 表示 box 中央 `W*z/2, H*z/2`、§1b)
  // は transform-origin (= wrapper の CSS box 中央) に任せず、式に直接書く。wrapper の
  // width/height は React の再描画 (`chrome.zoom`) で 1 フレーム遅れて追従するため、ズーム中は
  // box 中央が古い zoom のままの瞬間があり、回転中だけ canvas が揺れた (staging 実機 fb
  // 2026-10-03「回転したまま拡大縮小するとプルプル揺れる」)。
  el.style.transformOrigin = '0 0';
  el.style.transform =
    rotation === 0
      ? `translate3d(${tx}px, ${ty}px, 0)`
      : `translate3d(${tx + center.cx}px, ${ty + center.cy}px, 0) rotate(${rotation}deg) translate(${-center.cx}px, ${-center.cy}px)`;
  // wrapper の子 overlay (名前ラベル・カーソルサムネ・矢印等) が文字・矢印を画面に対して
  // 正立させるための逆回転量。各 overlay は `rotate(var(--oekaki-counter-rotation, 0deg))`
  // を自分のアンカー点まわりに当てる (React の再レンダリングを挟まずに追従できる)。
  el.style.setProperty(COUNTER_ROTATION_CSS_VAR, rotation === 0 ? '0deg' : `${-rotation}deg`);
};

/** wrapper に立てる「overlay 正立用の逆回転量」CSS custom property 名。 */
export const COUNTER_ROTATION_CSS_VAR = '--oekaki-counter-rotation';

/** overlay の `transform` 末尾に付ける逆回転 (アンカー点は `transform-origin` で指定)。 */
export const COUNTER_ROTATE_TRANSFORM = `rotate(var(${COUNTER_ROTATION_CSS_VAR}, 0deg))`;

/**
 * ドットバイドット表示 (割り切れない dpr) 用: wrapper の最終的な画面位置
 * `origin + offset` が device pixel の境界に乗るよう offset を丸める。
 * 倍率が合っていても枠の位置がドットの境目からずれると再サンプリングでぼやけるため。
 * `origin` = 表示枠 content box 原点の client 座標 (CSS px)。dpr が不正なら無加工。
 * 丸めは offset を最大 0.5 / dpr CSS px (= 半ドット) 動かすだけ。
 */
export const snapOffsetToDevicePixel = (
  offset: ViewportOffset,
  origin: ViewPoint,
  dpr: number,
): ViewportOffset => {
  if (!Number.isFinite(dpr) || dpr <= 0) return offset;
  const snap = (o: number, v: number): number => Math.round((o + v) * dpr) / dpr - o;
  return { tx: snap(origin.x, offset.tx), ty: snap(origin.y, offset.ty) };
};
